import { createContext, useContext, useEffect, useState, useCallback, useRef, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { queryClient } from "@/lib/queryClient";
import { effectiveRole, type CmsRole, type AccountStatus } from "@/lib/cmsAccess";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  role: CmsRole | null;
  status: AccountStatus;
  isAdmin: boolean;
  canEdit: boolean;
  loading: boolean;
  accessError: string | null;
  refreshAccess: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [role, setRole] = useState<CmsRole | null>(null);
  const [status, setStatus] = useState<AccountStatus>("active");
  const [loading, setLoading] = useState(true);
  const [accessError, setAccessError] = useState<string | null>(null);
  const checkRef = useRef(0);
  const userIdRef = useRef<string | null>(null);

  const loadAccess = useCallback(async (userId: string | null) => {
    const id = ++checkRef.current;
    if (!userId) { setRole(null); setStatus("active"); setAccessError(null); setLoading(false); return; }
    let lastError: string | null = null;
    for (let attempt = 0; attempt < 2; attempt++) {
      const { data, error } = await supabase.rpc("my_access");
      if (checkRef.current !== id) return;
      if (!error) {
        const d = (data ?? {}) as { role?: string | null; status?: string };
        const st: AccountStatus = d.status === "suspended" ? "suspended" : "active";
        setStatus(st);
        setRole(effectiveRole(d.role, st));
        setAccessError(null);
        setLoading(false);
        return;
      }
      lastError = error.message;
      await new Promise((r) => setTimeout(r, 600));
    }
    if (checkRef.current !== id) return;
    setRole(null);
    setAccessError(`Could not verify your access: ${lastError}`);
    setLoading(false);
  }, []);

  useEffect(() => {
    let mounted = true;
    // Never call the backend inside the auth callback itself: the auth client holds
    // a lock during the callback, so awaiting a query there can deadlock (infinite spinner).
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, newSession) => {
      if (!mounted) return;
      const newId = newSession?.user?.id ?? null;
      setSession(newSession);
      if (newId === userIdRef.current && event !== "SIGNED_OUT" && event !== "INITIAL_SESSION") return; // token refresh etc.
      if (userIdRef.current !== newId) queryClient.clear(); // drop cached data on logout/account change
      userIdRef.current = newId;
      setUser(newSession?.user ?? null);
      if (newId) setLoading(true);
      setTimeout(() => { if (mounted) loadAccess(newId); }, 0);
    });
    const timeout = setTimeout(() => {
      if (mounted) setLoading((l) => { if (l) setAccessError("Signing in is taking longer than expected. Reload the page to try again."); return false; });
    }, 12000);
    return () => { mounted = false; clearTimeout(timeout); subscription.unsubscribe(); };
  }, [loadAccess]);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error as Error | null };
  };

  const signOut = async () => {
    checkRef.current++;
    setRole(null);
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{
      user, session, role, status, loading, accessError,
      isAdmin: role === "admin", canEdit: role === "admin" || role === "editor",
      refreshAccess: () => loadAccess(userIdRef.current), signIn, signOut,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within an AuthProvider");
  return context;
}
